import React from 'react';
import { ArrowRight, Trash2 } from 'lucide-react';

const SchoolComparisonCard = ({ college, tier, studentProfile, fitScores, insights, aiInsights, loadingInsights, onDelete }) => {
  const gc = college.global_colleges || {};
  const schoolName = gc.name || college.name;
  const location = `${gc.city || ''}, ${gc.state || ''}`.trim();
  const enrollment = gc.student_enrollment ? `${(gc.student_enrollment / 1000).toFixed(1)}k undergrads` : '';

  // Helper function to clean and format text
  const cleanText = (text) => {
    if (!text) return '';

    // Handle various input types
    let cleanedText = text;

    // If it's an object, try to extract meaningful content
    if (typeof text === 'object' && !Array.isArray(text)) {
      // Skip useless boolean fields like study_abroad:true
      const meaningfulEntries = Object.entries(text).filter(([key, value]) => {
        // Filter out boolean values and empty values
        return typeof value !== 'boolean' && value !== null && value !== '';
      });

      if (meaningfulEntries.length === 0) return '';

      // Convert to readable format
      cleanedText = meaningfulEntries
        .map(([key, value]) => {
          const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          return `${label}: ${value}`;
        })
        .join(', ');
    }

    // Convert to string if needed
    cleanedText = String(cleanedText);

    // Remove HTML tags
    cleanedText = cleanedText.replace(/<[^>]*>/g, '');

    // Clean up extra whitespace
    cleanedText = cleanedText.replace(/\s+/g, ' ').trim();

    return cleanedText;
  };

  // Helper to extract array items safely
  const extractArrayItem = (arr, index = 0) => {
    if (!arr) return '';
    if (Array.isArray(arr)) {
      return arr.length > index ? cleanText(arr[index]) : '';
    }
    return cleanText(arr);
  };

  // Tier colors
  const tierColors = {
    reach: {
      headerBg: 'bg-coral-50',
      headerBorder: 'border-slate-900',
      badge: 'bg-coral-600',
      cardBorder: 'border-slate-900'
    },
    target: {
      headerBg: 'bg-teal-50',
      headerBorder: 'border-slate-900',
      badge: 'bg-teal-600',
      cardBorder: 'border-slate-900'
    },
    safety: {
      headerBg: 'bg-gold-50',
      headerBorder: 'border-gold-200',
      badge: 'bg-gold-600',
      cardBorder: 'border-slate-900'
    }
  };

  const colors = tierColors[tier] || tierColors.target;

  // Default fit scores if not provided
  const defaultFitScores = {
    academicMatch: 85,
    culturalFit: 82,
    careerAlignment: 88
  };

  const scores = fitScores || defaultFitScores;

  // Get "Your Edge" from student advantages (still using existing data)
  const yourEdge = college.student_advantages && Array.isArray(college.student_advantages)
    ? college.student_advantages.slice(0, 3).map(adv => cleanText(adv)).filter(Boolean)
    : ['Strong academic profile alignment', 'Geographic diversity contribution', 'Unique extracurricular match'];

  // Use AI insights if available, otherwise show loading state or fallback
  const mayNotKnow = aiInsights?.mayNotKnow || [];
  const betterThanYouThink = aiInsights?.betterThanYouThink || '';
  const hiddenAdvantage = aiInsights?.hiddenAdvantage || '';

  return (
    <div className={`group school-card bg-white border-2 ${colors.cardBorder} rounded-lg overflow-hidden flex-shrink-0 relative`}>
      {/* Header */}
      <div className={`${colors.headerBg} px-4 py-3 border-b-2 ${colors.headerBorder}`}>
        <div className="flex items-center justify-between mb-1">
          <h4 className="font-bold text-slate-900 text-sm">{schoolName}</h4>
          <div className="flex items-center gap-2">
            <div className={`${colors.badge} text-white px-2 py-0.5 rounded-full text-xs font-semibold`}>
              {tier.charAt(0).toUpperCase() + tier.slice(1)}
            </div>
            {onDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm(`Remove ${schoolName} from your ${tier} tier?`)) {
                    onDelete(college.id);
                  }
                }}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-50 rounded-full"
                title="Delete college"
              >
                <Trash2 className="w-3.5 h-3.5 text-red-600" />
              </button>
            )}
          </div>
        </div>
        <p className="text-xs text-slate-600">
          {location}{enrollment ? ` • ${enrollment}` : ''}
        </p>
      </div>

      <div className="p-4">
        {/* School Fit Score */}
        <div className="bg-stone-50 border border-slate-900 rounded-lg p-3 mb-4">
          <div className="text-xs font-semibold text-slate-900 mb-2">School Fit Score</div>
          <div className="space-y-2">
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-900">Academic Match</span>
                <span className="font-semibold text-purple-600">{scores.academicMatch}%</span>
              </div>
              <div className="h-1.5 bg-purple-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-purple-400"
                  style={{ width: `${scores.academicMatch}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-900">Cultural Fit</span>
                <span className="font-semibold text-indigo-600">{scores.culturalFit}%</span>
              </div>
              <div className="h-1.5 bg-indigo-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-400"
                  style={{ width: `${scores.culturalFit}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-700">Career Alignment</span>
                <span className="font-semibold text-azure-700">{scores.careerAlignment}%</span>
              </div>
              <div className="h-1.5 bg-azure-100 rounded-full overflow-hidden">
                <div
                      className="h-full bg-azure-400"
                  style={{ width: `${scores.careerAlignment}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Your Edge */}
        {yourEdge && yourEdge.length > 0 && (
          <div className="rounded-r-lg p-3 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <h5 className="font-semibold text-slate-900 text-sm">Your Edge</h5>
            </div>
            <ul className="space-y-2 text-xs text-slate-700">
              {yourEdge.filter(e => e && e.trim()).slice(0, 3).map((edge, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="text-slate-600 mt-0.5">•</span>
                  <span>{edge}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* You May Not Have Considered - AI Generated */}
        {loadingInsights ? (
          <div className="rounded-r-lg p-3 mb-4 bg-slate-50 border border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <h5 className="font-semibold text-slate-900 text-sm">You May Not Have Considered</h5>
            </div>
            <p className="text-xs text-slate-500 italic">Generating insights...</p>
          </div>
        ) : mayNotKnow.length > 0 ? (
          <div className="rounded-r-lg p-3 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <h5 className="font-semibold text-slate-900 text-sm">You May Not Have Considered</h5>
            </div>
            {mayNotKnow.slice(0, 2).map((item, idx) => (
              <p key={idx} className={`text-xs text-slate-700 ${idx > 0 ? 'mt-2' : ''}`}>
                {item}
              </p>
            ))}
          </div>
        ) : null}

        {/* Why This Fits You Better Than You Think - AI Generated */}
        {loadingInsights ? (
          <div className="rounded-r-lg p-3 mb-4 bg-slate-50 border border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <h5 className="font-semibold text-slate-900 text-sm">Why This Fits You Better Than You Think</h5>
            </div>
            <p className="text-xs text-slate-500 italic">Generating insights...</p>
          </div>
        ) : betterThanYouThink ? (
          <div className="rounded-r-lg p-3 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <h5 className="font-semibold text-slate-900 text-sm">Why This Fits You Better Than You Think</h5>
            </div>
            <p className="text-xs text-slate-700">
              {betterThanYouThink}
            </p>
          </div>
        ) : null}

        {/* The Hidden Advantage - AI Generated NEW SECTION */}
        {loadingInsights ? (
          <div className="rounded-r-lg p-3 mb-4 bg-slate-50 border border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <h5 className="font-semibold text-slate-900 text-sm">The Hidden Advantage</h5>
            </div>
            <p className="text-xs text-slate-500 italic">Generating insights...</p>
          </div>
        ) : hiddenAdvantage ? (
          <div className="rounded-r-lg p-3 mb-4 bg-indigo-50 border border-indigo-200">
            <div className="flex items-center gap-2 mb-2">
              <h5 className="font-semibold text-indigo-900 text-sm">The Hidden Advantage</h5>
            </div>
            <p className="text-xs text-indigo-900">
              {hiddenAdvantage}
            </p>
          </div>
        ) : null}

        {/* Button */}
        <button className="w-full py-2 bg-slate-900 text-white border-2 border-slate-900 rounded-lg text-xs font-medium hover:bg-slate-800 transition flex items-center justify-center gap-2">
          View Full Analysis
          <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

export default SchoolComparisonCard;
